import React, { useState, useEffect, useRef, forwardRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/locationPicker.css';

// Fix for default leaflet markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png',
});

const LocationPicker = forwardRef(({ onLocationSelect, selectedLocation }, ref) => {
  const [location, setLocation] = useState(selectedLocation);
  const [radius, setRadius] = useState(50);
  const [address, setAddress] = useState('');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const centerLat = location?.latitude || 14.5995;
      const centerLon = location?.longitude || 120.9842;

      const map = L.map(mapRef.current).setView([centerLat, centerLon], 12);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      mapInstanceRef.current = map;

      // Add click handler
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        handleLocationSelect({ latitude: lat, longitude: lng, accuracy: 100 });
      });

      return () => {
        // Don't fully destroy on unmount to avoid double init
      };
    }
  }, []);

  // Update marker and circle when location changes
  useEffect(() => {
    if (mapInstanceRef.current && location) {
      const map = mapInstanceRef.current;

      // Remove old marker
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
      }

      // Remove old circle
      if (circleRef.current) {
        map.removeLayer(circleRef.current);
      }

      // Add new marker
      markerRef.current = L.marker([location.latitude, location.longitude]).addTo(map);

      // Add new circle
      circleRef.current = L.circle([location.latitude, location.longitude], {
        color: 'blue',
        fill: true,
        fillColor: 'lightblue',
        fillOpacity: 0.2,
        radius: radius * 1000,
      }).addTo(map);

      // Center map on marker
      map.setView([location.latitude, location.longitude], 12);
    }
  }, [location, radius]);

  useEffect(() => {
    if (useCurrentLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = { latitude, longitude };
          setLocation(newLocation);
          onLocationSelect(newLocation);
          reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your current location');
        }
      );
    }
  }, [useCurrentLocation, onLocationSelect]);

  const handleLocationSelect = (newLocation) => {
    setLocation(newLocation);
    onLocationSelect(newLocation);
    reverseGeocode(newLocation.latitude, newLocation.longitude);
  };

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
      );
      const data = await response.json();
      setAddress(data.address?.city || data.address?.county || 'Selected Location');
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      setAddress(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
    }
  };

  const handleRadiusChange = (e) => {
    setRadius(parseInt(e.target.value));
  };

  return (
    <div className="location-picker" ref={ref}>
      <div className="location-controls">
        <button
          className="btn-current-location"
          onClick={() => setUseCurrentLocation(!useCurrentLocation)}
        >
          <span className="icon">📍</span>
          Use Current Location
        </button>
      </div>

      <div className="map-container">
        <div ref={mapRef} className="leaflet-map" style={{ height: '350px', width: '100%' }} />
      </div>

      <div className="location-details">
        {address && (
          <div className="address-display">
            <strong>Location:</strong> {address}
          </div>
        )}

        <div className="radius-control">
          <label>Search Radius: {radius} km</label>
          <input
            type="range"
            min="5"
            max="100"
            step="5"
            value={radius}
            onChange={handleRadiusChange}
            className="radius-slider"
          />
          <div className="radius-labels">
            <span>5 km</span>
            <span>50 km</span>
            <span>100 km</span>
          </div>
        </div>

        <p className="location-hint">
          💡 Click on the map to pin your exact location
        </p>
      </div>
    </div>
  );
});

LocationPicker.displayName = 'LocationPicker';

export default LocationPicker;
